import type { PropsWithChildren } from 'react';
import { useState } from 'react';

import { useLoaderData, useLocation, useParams, useSearchParams } from 'react-router';

import { Transition } from '@headlessui/react';
import { motion } from 'framer-motion';

import { TrackViewVariantList } from '@boc/analyticsV3';
import { TwMaxWidthPicture } from '@boc/images';
import type {
  ProductCardFragment,
  ProductVariantCardFragment,
} from '@boc/framework/saleor-graphql';
import type { Breadcrumb } from '@boc/framework/types';

import type { Route } from '../routes/+types/search';

import { cn } from '~/styling';
import { absoluteify, imgUrl, urls } from '~/urls';

import { Breadcrumbs } from '~/local/components/breadcrumbs';
import { getCategoryBreadcrumbs } from '~/local/products/breadcrumbs';
import { VariantGrid } from '~/local/products/components/product-grid';
import { FilterForm, MobileSidebar } from '~/local/products/search/components/filter';
import { ShowHideFilters } from '~/local/products/search/components/filter-button';
import { Pagination, ResetPagination } from '~/local/products/search/components/pagination';

const sortOptions = [
  { label: 'Magic', value: 'rating', direction: 'desc' },
  { label: 'Newest', value: 'date', direction: 'desc' },
  { label: 'Name (A-Z)', value: 'name', direction: 'asc' },
  { label: 'Name (Z-A)', value: 'name', direction: 'desc' },
  { label: 'Price: Low to High', value: 'price', direction: 'asc' },
  { label: 'Price: High to Low', value: 'price', direction: 'desc' },
] as const;

import { DEFAULT_HERO_IMAGE } from '#/products/images';
import { DEFAULT_TITLE } from '#/products/seo';

import type { loader } from '../routes/search';

const SortSelect = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentSort = searchParams.get('sort') ?? 'rating';
  const currentDirection = searchParams.get('direction') ?? 'desc';
  const currentIndex = sortOptions.findIndex(
    (o) => o.value === currentSort && o.direction === currentDirection
  );
  const selectedIndex = currentIndex >= 0 ? currentIndex : 0;

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const option = sortOptions[parseInt(e.target.value, 10)];
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('sort', option.value);
      next.set('direction', option.direction);
      next.delete('after');
      return next;
    });
  };

  return (
    <div className="flex items-center gap-2">
      <span className="text-sm">Sort by:</span>
      <select
        value={selectedIndex}
        onChange={handleChange}
        className="border-neutral-2 rounded border bg-transparent py-1 pl-2 pr-6 text-sm"
      >
        {sortOptions.map((option, i) => (
          <option key={i} value={i}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
};

const ProductResultLayout = ({
  children,
  heading,
  breadcrumbs,
  viewBy,
  setViewBy,
}: PropsWithChildren<{
  heading: string;
  breadcrumbs: Breadcrumb[];
  viewBy: 'art' | 'box';
  setViewBy: (view: 'art' | 'box') => void;
}>) => {
  const params = useParams();

  const [showDesktopFilters, setShowDesktopFilters] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const { categories, attributes, initialSearchState } = useLoaderData<typeof loader>();

  const allBreadcrumbs = [
    {
      name: 'Home',
      url: urls.home(params),
    },
    {
      name: 'Shop',
      url: urls.products.index(params),
    },
    ...breadcrumbs,
  ];

  return (
    <main className="text-primary-2-80">
      <Breadcrumbs breadcrumbs={allBreadcrumbs} />

      <div className="relative -mx-6 flex h-80 flex-col justify-center md:mx-0 lg:h-[450px]">
        <TwMaxWidthPicture
          imgUrl={imgUrl}
          src={DEFAULT_HERO_IMAGE}
          maxWidth="lg"
          alt={DEFAULT_TITLE}
          className="absolute inset-0 -z-10 h-full w-full object-cover"
        />
        <div className="font-heading text-neutral-1 z-10 p-6 text-xl leading-7 font-medium tracking-tight lg:p-12 lg:text-4xl lg:leading-9">
          Explore our beautiful,
          <br />
          handpicked jigsaws
        </div>
      </div>

      <div className="mt-14 space-y-10">
        {/* TODO: Make dynamic */}
        {/*
          <div className="flex flex-wrap place-content-center items-center gap-2">
            <button className={buttonVariants({ intent: 'filter' })}>All Puzzles</button>
            <button className={buttonVariants({ intent: 'filter' })}>New</button>
            <button className={buttonVariants({ intent: 'filter' })}>JSN Originals</button>
            <button className={buttonVariants({ intent: 'filter' })}>Bestsellers</button>
            <button className={buttonVariants({ intent: 'filter' })}>Matchbox Puzzles</button>
            <button className={buttonVariants({ intent: 'filter' })}>Space Puzzles</button>
          </div>
        */}
        <div className="border-b-neutral-2-20 w-full border-b" />
        <div className="font-heading flex flex-col justify-between gap-4 leading-4 tracking-tight md:flex-row md:items-center">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:gap-6">
            <ShowHideFilters
              showDesktop={showDesktopFilters}
              setShowDesktop={setShowDesktopFilters}
              showMobile={showMobileFilters}
              setShowMobile={setShowMobileFilters}
            />
            {/* <div className="">{results?.totalCount} products</div> */}
          </div>
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:gap-6">
            <div className="flex items-center gap-2">
              <span>View by:</span>
              <div className="border-neutral-2 relative flex rounded-full border p-0 text-xs">
                <motion.div
                  className="bg-secondary-1 absolute inset-y-0 rounded-full"
                  initial={false}
                  animate={{
                    left: viewBy === 'art' ? '0' : '50%',
                  }}
                  transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                  style={{ width: '50%' }}
                />
                <button
                  onClick={() => setViewBy('art')}
                  className={cn(
                    'relative z-10 rounded-full px-4 py-1 text-sm font-semibold transition-colors',
                    viewBy === 'art' ? 'text-neutral-1' : 'text-neutral-2'
                  )}
                >
                  ART
                </button>
                <button
                  onClick={() => setViewBy('box')}
                  className={cn(
                    'relative z-10 rounded-full px-4 py-1 text-sm font-semibold transition-colors',
                    viewBy === 'box' ? 'text-neutral-1' : 'text-neutral-2'
                  )}
                >
                  BOX
                </button>
              </div>
            </div>
          </div>
          <SortSelect />
        </div>
      </div>

      <div className="my-14 lg:flex">
        <Transition
          show={showDesktopFilters}
          as={'div'}
          enter="transition ease-in-out duration-300 transform origin-left"
          enterFrom="scale-x-0 opacity-0"
          enterTo="scale-x-100 opacity-100"
          leave="transition ease-in-out duration-300 transform origin-left"
          leaveFrom="scale-x-100 opacity-100"
          leaveTo="scale-x-0 opacity-0"
        >
          <FilterForm
            initialSearchState={initialSearchState}
            categories={categories}
            attributes={attributes}
            setShowFilters={setShowDesktopFilters}
          />
        </Transition>

        <MobileSidebar heading="Filter By" show={showMobileFilters} setShow={setShowMobileFilters}>
          <FilterForm
            initialSearchState={initialSearchState}
            categories={categories}
            attributes={attributes}
            setShowFilters={setShowMobileFilters}
          />
        </MobileSidebar>

        <div className="w-full space-y-6">{children}</div>
      </div>
    </main>
  );
};

export function ProductResults({ loaderData, params }: Route.ComponentProps) {
  const { products, bestsellers, pageInfo, category, attribute } = loaderData;

  const { state } = useLocation();

  const [viewBy, setViewBy] = useState<'art' | 'box'>('art');

  let variants: ProductVariantCardFragment[] = [];

  if (state?.previousProducts) {
    variants = [...state.previousProducts, ...products];
  } else {
    variants = products;
  }

  // Compute next cursor for pagination
  const next = pageInfo.hasNextPage ? pageInfo.endCursor || undefined : undefined;

  // Generate breadcrumbs based on the context
  let breadcrumbs: Breadcrumb[] = [];

  if (category && attribute && params.valueSlug) {
    // For attribute filter pages
    // Find the selected value from the attribute's choices
    const selectedValue = attribute.choices?.edges.find(
      (edge) => edge.node.slug === params.valueSlug
    )?.node;

    if (selectedValue) {
      breadcrumbs = [
        {
          name: category.name,
          url: urls.products.categories(params, { slug: category.slug }),
        },
        {
          name: `${attribute.name || attribute.slug} - ${selectedValue.name || selectedValue.slug}`,
          url: urls.products.attributeWithCategory(params, {
            categorySlug: category.slug,
            attributeSlug: params.attributeSlug || attribute.slug || '',
            valueSlug: params.valueSlug,
          }),
        },
      ];
    } else {
      // Fallback to category breadcrumbs if value not found
      breadcrumbs = getCategoryBreadcrumbs(category, params).slice(2);
    }
  } else if (category) {
    // For category pages
    breadcrumbs = getCategoryBreadcrumbs(category, params).slice(2); // Remove Home and Shop
  }

  // Show bestsellers when no products found
  if (variants.length === 0 && bestsellers.length > 0) {
    return (
      <ProductResultLayout heading="" breadcrumbs={breadcrumbs}>
        <div className="space-y-6">
          <div className="bg-primary-2-10 rounded-lg p-6 text-center">
            <h2 className="font-heading text-primary-2-80 text-xl font-semibold">
              No products found
            </h2>
            <p className="text-primary-2-60 mt-2">
              We couldn't find any products matching your filters. Check out our bestsellers below!
            </p>
          </div>
          <h3 className="font-heading text-primary-2-80 text-2xl font-bold">Bestsellers</h3>
          {/* <VariantGrid variants={bestsellers} prefetchCount={6} /> */}
        </div>
      </ProductResultLayout>
    );
  }

  // Show empty message if no products and no bestsellers
  if (variants.length === 0) {
    return (
      <ProductResultLayout
        heading=""
        breadcrumbs={breadcrumbs}
        viewBy={viewBy}
        setViewBy={setViewBy}
      >
        <div className="bg-primary-2-10 rounded-lg p-12 text-center">
          <h2 className="font-heading text-primary-2-80 text-xl font-semibold">
            No products found
          </h2>
          <p className="text-primary-2-60 mt-2">
            We couldn't find any products matching your filters. Try adjusting your search criteria.
          </p>
        </div>
      </ProductResultLayout>
    );
  }

  // Generate list ID and name based on category or attribute context
  const listId = category?.slug || 'search-results';
  const listName = category?.name || 'Search Results';

  return (
    <ProductResultLayout heading="" breadcrumbs={breadcrumbs} viewBy={viewBy} setViewBy={setViewBy}>
      <div className="space-y-4">
        <ResetPagination />
        <TrackViewVariantList
          variants={variants}
          itemUrl={(v) => absoluteify(urls.products.details(params, { slug: v.product.slug }))}
          listId={listId}
          listName={listName}
        />
        <VariantGrid variants={variants} artStyle={viewBy} prefetchCount={6} />
        <Pagination next={next} products={variants} />
      </div>
    </ProductResultLayout>
  );
}
